export const projectStatuses = ['Active', 'Planning', 'On Hold', 'Completed'] as const;

export const approvalStatuses = ['Pending', 'Approved', 'Rejected'] as const;

/** Sample collection method types — code stored in DB, full name displayed in UI */
export const sampleTypeEntries = [
  { code: 'A', label: 'Amalgamated/Composite' },
  { code: 'SST', label: 'Split Spoon (twinned)' },
  { code: 'P', label: 'Piston' },
  { code: 'W', label: 'Water' },
  { code: 'B', label: 'Block' },
  { code: 'SS', label: 'Split Spoon' },
  { code: 'BK', label: 'Bulk' },
  { code: 'SB', label: 'Shelby Tube' },
  { code: 'GrabT', label: 'Grab (twinned)' },
  { code: 'SBT', label: 'Shelby Tube (twinned)' },
  { code: 'WD', label: 'Water (Duplicate)' },
  { code: 'L', label: 'Core Tube Liner' },
  { code: 'PT', label: 'Piston (twinned)' },
  { code: 'MZ', label: 'Mazier' },
  { code: 'R', label: 'Rock' },
  { code: 'Grab', label: 'Grab' },
] as const;

/** Full display names for dropdowns */
export const sampleTypes = sampleTypeEntries.map(e => e.label) as unknown as readonly string[];

/** Map code ↔ label for CSV import/export */
export const sampleTypeCodeToLabel = Object.fromEntries(sampleTypeEntries.map(e => [e.code, e.label]));
export const sampleTypeLabelToCode = Object.fromEntries(sampleTypeEntries.map(e => [e.label, e.code]));

// TASK-006A: 'In Transit' is the earliest stage in the sample lifecycle.
export const sampleStatuses = ['In Transit', 'Received', 'In Testing', 'Completed', 'Rejected'] as const;

// TASK-006B: priorities as P1, P2, P3 (P1=highest). Custom priorities can be
// added at runtime via localStorage; this is the seed list.
const DEFAULT_PRIORITIES = ['P1', 'P2', 'P3'];
function loadCustomPriorities(): string[] {
  try {
    const raw = localStorage.getItem('belab-custom-priorities');
    if (!raw) return DEFAULT_PRIORITIES;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((x: unknown) => typeof x === 'string')) return parsed;
  } catch { /* ignore */ }
  return DEFAULT_PRIORITIES;
}
export let priorities: string[] = loadCustomPriorities();

/** Add a new custom priority and persist. Returns the updated list. */
export function addCustomPriority(label: string): string[] {
  const trimmed = label.trim();
  if (!trimmed || priorities.includes(trimmed)) return priorities;
  priorities = [...priorities, trimmed];
  localStorage.setItem('belab-custom-priorities', JSON.stringify(priorities));
  return priorities;
}

/** Persist the full priority list (for reordering etc.). */
export function saveCustomPriorities(list: string[]): void {
  priorities = list;
  localStorage.setItem('belab-custom-priorities', JSON.stringify(list));
}

/** Map legacy text priorities to Pn format so historical data still renders. */
export function normalizePriority(p: string | null | undefined): string {
  if (!p) return 'P2';
  const s = String(p).trim();
  if (s === 'High' || s === '1') return 'P1';
  if (s === 'Normal' || s === '2') return 'P2';
  if (s === 'Low' || s === '3') return 'P3';
  return s;
}

export const conditions = ['Excellent', 'Good', 'Fair', 'Poor'] as const;

export const locationTypes = ['Borehole', 'Test Pit'] as const;

export const workOrderStatuses = ['Scheduled', 'In Progress', 'On Hold', 'Completed', 'Cancelled'] as const;

// BUG-2026-0190: 'Rejected' is a terminal test status (modeled on the sample
// 'Rejected' status in sampleStatuses) — a test that has been rejected and will
// not proceed. It is deliberately NOT part of the signoff chain
// (lifecycleService.PROTECTED_STATUSES), so it never triggers a
// forward/rollback transition; it is a side/terminal state like a rejected
// sample. The old 'Delete' status option was removed in the same ticket (it had
// no behaviour beyond confusing the dropdown — soft delete is sysLastAction).
export const testStatuses = [
  'Scheduled', 'In Progress', 'Test Completed',
  'Request Review', 'Review Completed', 'Approved', 'Rejected',
] as const;

export const testTypes = [
  'Direct Shear Test',
  'Triaxial Test (CU)',
  'Triaxial Test (CD)',
  'Rock Triaxial Compressive Strength',
  'Rock Brazilian Tensile Strength',
  'Rock Uniaxial Compressive Strength',
  'Rock Uniaxial Compressive Strength with Modulus',
  'Cerchar Abrasivity Test',
  'Consolidation Test',
  'Atterberg Limits',
  'Particle Size Distribution',
] as const;

/**
 * Test types that have full workflow implemented (by display name).
 *
 * DISPLAY/reference only. Runtime dispatch (the TestWorkflowPage "Under
 * Construction" gate) now keys on the stable code set `IMPLEMENTED_TEST_CODES`
 * in `constants/testCodes.ts` (BUG-2026-0197) so a renamed test still resolves.
 */
export const implementedTestTypes: ReadonlySet<string> = new Set([
  'Direct Shear Test',
  'Atterberg Limits',
  'Particle Size Distribution',
  'Triaxial Test (CU)',
  'Triaxial Test (CD)',
  'Rock Triaxial Compressive Strength',
  'Rock Brazilian Tensile Strength',
  'Rock Uniaxial Compressive Strength',
  'Rock Uniaxial Compressive Strength with Modulus',
  'Point Load Test',
  'Cerchar Abrasivity Test',
]);

export const newProjectStatuses = ['Active', 'Planning', 'On Hold'] as const;

// BUG-2026-0190: the WO-grid Test Status dropdown now uses `testStatuses`
// directly. The former `testStatusesWithDelete` list (which appended a bogus
// 'Delete' option) was removed — 'Delete' was never a real lifecycle status and
// had no server-side behaviour; deleting a test is a soft delete
// (sysLastAction='delete'), not a status.

// ── Quotes ──
export const quoteStatuses = ['Draft', 'Internal Review', 'Approved Internal', 'Sent', 'Viewed', 'Accepted', 'Declined', 'Expired', 'Withdrawn', 'Converted'] as const;
export const currencyOptions = ['CAD', 'USD'] as const;
export const taxTypes = ['None', 'GST', 'HST', 'PST+GST'] as const;
export const discountTypes = ['None', 'Percent', 'Fixed'] as const;

// ── Feedback ──
// The flow is:
//   New → Ready for Testing → (Confirmed Fixed | Needs Rework → Ready for Testing → …)
// 'Ready for Testing' — AI/admin posted a fix; submitter needs to verify.
// 'Needs Rework'      — submitter rejected the proposed solution; AI/admin retries.
// 'Waiting on Submitter' — admin/AI asked the submitter a question and work is
//                       parked until they answer. A submitter comment auto-flips
//                       the ticket back to 'Needs Rework' (solution attempted)
//                       or 'New' (no attempt yet) — no manual re-triage needed.
// 'Confirmed Fixed'   — submitter accepted. Ticket is locked from further
//                       submitter edits (no comments, no attachments, no flips).
// 'Fixed'             — admin marked fixed without a Ready-for-Testing round.
// 'Deferred'          — won't fix / postponed.
export const feedbackStatuses = [
  'New',
  'Ready for Testing',
  'Needs Rework',
  'Waiting on Submitter',
  'Fixed',
  'Confirmed Fixed',
  'Deferred',
] as const;
export const bugSeverities = ['Low', 'Medium', 'High', 'Critical'] as const;
export const featurePriorities = ['Nice to Have', 'Important', 'Essential'] as const;
export const adminPriorities = ['P1', 'P2', 'P3', 'P4'] as const;

/** Fallback until dynamic team members load from API */
export const teamMembers = [
  'Vahid Haeri',
] as const;

// ── Schedule: Machines ──
export const machineStatusOptions = [
  'Active',
  'OutOfService',
  'Calibration',
  'Maintenance',
  'Decommissioned',
] as const;

export const machineStatusLabels: Record<string, string> = {
  Active: 'Active',
  OutOfService: 'Out of Service',
  Calibration: 'Calibration',
  Maintenance: 'Maintenance',
  Decommissioned: 'Decommissioned',
};

// Migration 120 (machine taxonomy): LabSection is the TOP category of the
// two-level machine grouping — exactly Soil / Rock / Index. The finer test
// family lives in Machine.MachineGroup (free text, admin-editable).
export const labSectionOptions = [
  'Soil',
  'Rock',
  'Index',
] as const;
